import { useEffect, useRef, useState } from 'react';

/**
 * Formatter-bound numeric input for the Costing / Pricing grids — faithful
 * re-platform of `fn.uni_tools.bindFormatter` (formatter.js FormatterBinder /
 * NumericInputFormatter), which both tabs bind to EVERY data-formatter_type'd
 * input (§B2/§B3, parity 160):
 *
 *  - display encode: comma-grouped integer part; integer → NO fraction
 *    (TRUNCATED, not rounded), decimal → 2dp, float → 4dp; null/'' → ''.
 *  - focus: un-groups the value to `toFixed(dp)` for editing (only when the
 *    decoded value is truthy — 0/empty stay as-is) + revalidates.
 *  - change (here: blur-with-edit): decode → `.invalid` from the PRE-clamp
 *    value (updateValidity quirk: an out-of-range entry keeps `.invalid` even
 *    though the display shows the clamp) → intoRange clamp → re-encoded
 *    write-back → commit the CLAMPED decoded value (null when empty).
 *  - blur without edit: re-encode the display (formatter onblur setValue).
 *
 * `onBackspace` reproduces the LIVE pricing `keydown_backspace` (§B3, parity
 * 166): keyCode 8 on a `== 0` value → plain display reset (the legacy
 * re-render); otherwise the CURRENT (pre-deletion, UNCLAMPED) decoded value is
 * committed through the change path while the deletion proceeds. The costing
 * twin is the window.name bug — [NEG], so CostingTab simply doesn't pass it.
 *
 * `output` renders the read-only display variant; `readOnlyAttr` controls
 * whether the literal `readonly` attribute is emitted (pricing markup has it,
 * costing's read-only cells rely on `.non-clickable` + tabindex -1 only).
 */
export type FormatterMode = 'integer' | 'decimal' | 'float';

const dpOf = (mode: FormatterMode): number =>
  mode === 'decimal' ? 2 : mode === 'float' ? 4 : 0;

/** FormatterBinder.decode: keep digits/./-, '' → null, residue → 0. */
export const decodeFormatted = (raw: string | number | null | undefined): number | null => {
  const s = raw == null ? '' : String(raw);
  if (s === '') return null;
  let res = '';
  let started = false;
  for (const c of s) {
    if ((!Number.isNaN(Number(c)) && c !== ' ') || c === '.' || c === '-') {
      if (!started && c && !Number.isNaN(Number(c))) started = true;
      if (c === '0' && !started) continue;
      if (c === '-' && res) continue;
      res += c;
    }
  }
  if (!res || res === '.' || res === '-') return 0;
  return parseFloat(res);
};

/** FormatterBinder.encode: comma grouping + 0/2/4dp (integer TRUNCATES). */
export const encodeFormatted = (decoded: number | null, mode: FormatterMode): string => {
  if (decoded == null) return '';
  let isNegative = decoded < 0;
  const v = isNegative ? -decoded : decoded;
  const parts = v.toString().split('.');
  let left = parts[0]
    .split('')
    .reverse()
    .map((e, i, arr) => ((i + 1) % 3 === 0 && i !== arr.length - 1 ? `,${e}` : e))
    .reverse()
    .join('');
  let right = '';
  if (mode === 'decimal' && !Number.isNaN(v)) {
    right = v.toFixed(2).split('.')[1];
  } else if (mode === 'float' && !Number.isNaN(v)) {
    right = v.toFixed(4).split('.')[1];
  }
  if (!left) {
    if (right) left = '0';
    else isNegative = false;
  }
  return (isNegative ? '-' : '') + left + (right ? `.${right}` : '');
};

/** Template value → formatted display ({{value}} of null/undefined → ''). */
export const displayFormatted = (
  value: number | string | null | undefined,
  mode: FormatterMode
): string => {
  if (value === null || value === undefined || value === '') return '';
  return encodeFormatted(
    typeof value === 'number' ? value : decodeFormatted(value),
    mode
  );
};

interface FormattedNumberInputProps {
  name?: string;
  value: number | string | null | undefined;
  mode: FormatterMode;
  className: string;
  min?: number | string;
  max?: number | string;
  step?: number | string;
  boundParameter?: string;
  /** Read-only display cell (no commit path, onChange noop). */
  output?: boolean;
  /** Emit the literal `readonly` attribute (pricing read-only markup). */
  readOnlyAttr?: boolean;
  tabIndex?: number;
  disabled?: boolean;
  onCommit?: (name: string, value: number | null) => void;
  /** Pricing live keydown_backspace (decoded pre-deletion value, UNCLAMPED). */
  onBackspace?: (name: string, value: number | null) => void;
}

const FormattedNumberInput = (props: FormattedNumberInputProps) => {
  const { mode } = props;
  const synced = displayFormatted(props.value, mode);
  const [draft, setDraft] = useState(synced);
  const [invalid, setInvalid] = useState(false);
  const ref = useRef<HTMLInputElement | null>(null);
  const focusDraftRef = useRef<string | null>(null);

  useEffect(() => {
    if (document.activeElement !== ref.current) setDraft(synced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [synced]);

  const min = props.min != null ? parseFloat(String(props.min)) : NaN;
  const max = props.max != null ? parseFloat(String(props.max)) : NaN;
  const inRange = (n: number): boolean => {
    if (Number.isNaN(n)) return true;
    if (!Number.isNaN(min) && n < min) return false;
    if (!Number.isNaN(max) && n > max) return false;
    return true;
  };
  const intoRange = (n: number): number => {
    let v = n;
    if (!Number.isNaN(min) && v < min) v = min;
    if (!Number.isNaN(max) && v > max) v = max;
    return v;
  };

  const dataAttrs = {
    'data-formatter_type': mode,
    'data-min': props.min,
    'data-max': props.max,
    'data-step': props.step,
    'data-boundparameter': props.boundParameter,
  };

  if (props.output) {
    return (
      <input
        autoComplete="off"
        type="text"
        className={props.className}
        name={props.name}
        value={synced}
        readOnly={props.readOnlyAttr}
        tabIndex={props.tabIndex}
        disabled={props.disabled}
        {...dataAttrs}
        onChange={() => undefined}
      />
    );
  }

  const onFocus = () => {
    const d = decodeFormatted(draft);
    if (d) {
      // Legacy onfocus: only truthy values get un-grouped + revalidated.
      setInvalid(!inRange(d));
      const next = d.toFixed(dpOf(mode));
      setDraft(next);
      focusDraftRef.current = next;
    } else {
      focusDraftRef.current = draft;
    }
  };

  const onBlur = () => {
    if (draft !== (focusDraftRef.current ?? synced)) {
      // DOM change event path: formatter onchange (validity from the PRE-clamp
      // value, clamp + re-encode) then the view's change handler (commit).
      const d = decodeFormatted(draft);
      setInvalid(d != null && !inRange(d));
      const clamped = d == null ? null : intoRange(d);
      setDraft(encodeFormatted(clamped, mode));
      if (props.name) props.onCommit?.(props.name, clamped);
    } else {
      // Formatter onblur setValue: always re-group the display.
      setDraft(encodeFormatted(decodeFormatted(draft), mode));
    }
    focusDraftRef.current = null;
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!props.onBackspace || e.key !== 'Backspace') return;
    if (Number(draft) === 0) {
      // `e.target.value == 0` (loose; '' coerces to 0) → plain re-render:
      // the display resets from the model/set_values (legacy DOM rebuild).
      setDraft(synced);
      focusDraftRef.current = synced;
    } else if (props.name) {
      // Live commit of the PRE-deletion value (channge_input, no clamp —
      // the formatter doesn't run on keydown); the deletion still applies.
      props.onBackspace(props.name, decodeFormatted(draft));
    }
  };

  return (
    <input
      ref={ref}
      autoComplete="off"
      type="text"
      className={`${props.className}${invalid ? ' invalid' : ''}`}
      name={props.name}
      value={draft}
      tabIndex={props.tabIndex}
      disabled={props.disabled}
      {...dataAttrs}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
    />
  );
};

export default FormattedNumberInput;
